import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Observable, map } from 'rxjs';

import { Instrument } from '@prlw/core/monitor/instrument.entity';
import { SessionAgeStatus } from '@prlw/core/monitor/session-age-status.enum';
import { TariffStatus } from '@prlw/core/monitor/tariff-status.enum';
import {
  MainTableColumnId,
  ValueChanges,
} from '@prlw/core/monitor/types/main-table';
import { BasisDetailsInterface } from '../../../basis-details.interface';
import { BasisDetails } from '../../../basis-details.class';
import { DestinationState } from '@prlw/core/destinations/destination.state';
import { Destination } from '@prlw/core/destinations/destination.entity';
import { Modifier } from '@prlw/core/monitor/types/common';
import { MainTableModifiersService } from '@prlw/core/monitor/main-table-modifiers.service';
import { ProfileController } from '@prlw/core/profile/profile.controller';
import { EditTraderTariffOverlayService } from '../edit-trader-tariff/edit-trader-tariff-overlay.service';

export type Data = string | number | Date | null | undefined;

export type ColumnTypes = {
  isBasis: boolean;
  isOffer: boolean;
  isDeal: boolean;
  isDemand: boolean;
  isChanges: boolean;
  isVolume: boolean;
  isPrevSessionVolume: boolean;
  isPrevDealsAmountDiff: boolean;
  isTariff: boolean;
  isLocalPrice: boolean;
  isShipment: boolean;
  isOnTheWay: boolean;
  isTradeStart: boolean;
};

export type TariffDetails = {
  source: string | null;
  timestamp: string | null;
  stationDescription: string | null;
  deliveryMethod: string;
  shipmentBasisName: string;
  destinationName: Destination | null;
};

export type VolumeDifference = {
  isHigh: boolean;
  isLow: boolean;
};

export type ChangesDirection = {
  isHigh: boolean;
  isLow: boolean;
};

export type HighlightChangesDirection = {
  isHigh: boolean;
  isLow: boolean;
};

@Component({
  selector: 'prlw-table-column-content',
  templateUrl: './column-content.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnContentContainer {
  public modifier$: Observable<Modifier | null>;
  @Input() public columnId!: MainTableColumnId;
  @Input() public instrument!: Instrument;
  @Input() public valueChanges: ValueChanges | null = null;
  @Input() public isHovered = false;
  @Input() public isActive = false;
  private _currentDestination: Destination | null = null;

  constructor(
    private readonly datePipe: DatePipe,
    private readonly destinationState: DestinationState,
    private readonly profileController: ProfileController,
    mainTableModifiersService: MainTableModifiersService,
    private readonly editTraderTariffOverlayService: EditTraderTariffOverlayService,
  ) {
    this.modifier$ = mainTableModifiersService.modifier$;
    this.destination$.subscribe((destination) => {
      this._currentDestination = destination;
    });
  }

  handleOpenEditTraderTariffOverlay(event: string) {
    this.editTraderTariffOverlayService.open(event);
  }

  isEditor$ = this.profileController.profile$.pipe(
    map(({ roleEditor }) => roleEditor),
  );

  public get columnTypes(): ColumnTypes {
    return {
      isBasis: this.columnId === MainTableColumnId.BASIS,
      isOffer: this.columnId === MainTableColumnId.OFFER,
      isDeal: this.columnId === MainTableColumnId.DEAL,
      isDemand: this.columnId === MainTableColumnId.DEMAND,
      isChanges: this.columnId === MainTableColumnId.CHANGES,
      isVolume: this.columnId === MainTableColumnId.VOLUME,
      isPrevSessionVolume:
        this.columnId === MainTableColumnId.PREV_SESSION_VOLUME,
      isPrevDealsAmountDiff:
        this.columnId === MainTableColumnId.PREV_DEALS_AMOUNT_DIFF,
      isTariff: this.columnId === MainTableColumnId.TARIFF,
      isLocalPrice: this.columnId === MainTableColumnId.LOCAL_PRICE,
      isShipment: this.columnId === MainTableColumnId.SHIPMENT,
      isOnTheWay: this.columnId === MainTableColumnId.ON_THE_WAY,
      isTradeStart: this.columnId === MainTableColumnId.TRADE_START,
    };
  }

  public get hasData(): boolean {
    return !this._isNeedPlug || Boolean(this.columnData[0]);
  }

  public get hasSecondData(): boolean {
    return (
      this.columnTypes.isOffer ||
      this.columnTypes.isDemand ||
      this.columnTypes.isVolume
    );
  }

  public get isOfferActive(): boolean {
    return this.columnTypes.isOffer && this._isActialQuote;
  }

  public get isDemandActive(): boolean {
    return this.columnTypes.isDemand && this._isActialQuote;
  }

  public get isDealTotal(): boolean {
    return this.instrument.isDealTotal && this.columnTypes.isLocalPrice;
  }

  public get isLastDeal(): boolean {
    return (
      this._isLastDeal &&
      (this.columnTypes.isDeal || this.columnTypes.isLocalPrice)
    );
  }

  public get isOldDeal(): boolean {
    return (
      this._isOldDeal &&
      (this.columnTypes.isDeal || this.columnTypes.isLocalPrice)
    );
  }

  public get isLastQuote(): boolean {
    return (
      this._isLastQuote &&
      (this.columnTypes.isDemand || this.columnTypes.isOffer)
    );
  }

  public get isOldQuote(): boolean {
    return (
      this._isOldQuote &&
      (this.columnTypes.isDemand || this.columnTypes.isOffer)
    );
  }

  public get isTariffLoading(): boolean {
    return (
      this.columnTypes.isTariff &&
      this.instrument.tariffStatus === TariffStatus.Wait
    );
  }

  public get isTariffError(): boolean {
    return (
      this.columnTypes.isTariff &&
      this.instrument.tariffStatus === TariffStatus.Error
    );
  }

  public get destination$(): Observable<Destination | null> {
    return this.destinationState.currentDestination$;
  }

  public get basisTrend(): string | null {
    return this.instrument.forecastTrend;
  }

  public get dealTime(): string | null {
    return this.columnTypes.isDeal
      ? this.datePipe.transform(
          this.instrument.dealTimestamp,
          'dd.MM.yyyy H:mm:ss',
        )
      : null;
  }

  public get prevDealDate(): string | null {
    return this.columnTypes.isPrevSessionVolume
      ? this.datePipe.transform(
          this.instrument.previousSessionDate,
          'dd.MM.yyyy',
        )
      : null;
  }

  public get lastQuoteTime(): string | null {
    return this.columnTypes.isOffer || this.columnTypes.isDemand
      ? this.datePipe.transform(
          this.instrument.quote.timestamp,
          'dd.MM.yyyy H:mm:ss',
        )
      : null;
  }

  public get isDealTotalPrice(): boolean {
    return (
      this.isDealTotal &&
      !this._isOldDeal &&
      !this._isLastDeal &&
      this.columnTypes.isLocalPrice
    );
  }

  public get volumeDifference(): VolumeDifference {
    let isHigh = false;
    let isLow = false;
    if (this.columnTypes.isVolume && this.columnData[0] && this.columnData[1]) {
      if (this.columnData[0] > this.columnData[1]) {
        isHigh = true;
      } else {
        isLow = true;
      }
    }

    return {
      isHigh,
      isLow,
    };
  }

  public get changesDirection(): ChangesDirection {
    let isHigh = false;
    let isLow = false;
    if (this.columnTypes.isChanges && this.columnData[0]) {
      if (Number(this.columnData[0]) > 0) {
        isHigh = true;
      } else {
        isLow = true;
      }
    }

    return {
      isHigh,
      isLow,
    };
  }

  public get prevDealsAmountDiffDirection(): ChangesDirection {
    let isHigh = false;
    let isLow = false;
    if (this.columnTypes.isPrevDealsAmountDiff && this.columnData[0]) {
      if (Number(this.columnData[0]) > 0) {
        isHigh = true;
      } else {
        isLow = true;
      }
    }
    return {
      isHigh,
      isLow,
    };
  }

  public get basisDetails(): BasisDetailsInterface | null {
    return this.columnTypes.isBasis
      ? BasisDetails.fromInstrument(this.instrument)
      : null;
  }

  public get tariffDetails(): TariffDetails | null {
    return this.columnTypes.isTariff
      ? {
          source: this.instrument.tariffSource,
          timestamp: this.instrument.tariffTimestamp,
          stationDescription: this.instrument.stationDescription,
          deliveryMethod: this.instrument.deliveryMethod,
          shipmentBasisName: this.instrument.shipment.name,
          destinationName: this._currentDestination,
        }
      : null;
  }

  public get highlightChangesDirection(): HighlightChangesDirection {
    let isHigh = false;
    let isLow = false;

    if (this.columnTypes.isOffer) {
      isHigh = this.valueChanges?.isOfferUp === true;
      isLow = this.valueChanges?.isOfferUp === false;
    } else if (this.columnTypes.isDeal) {
      isHigh = this.valueChanges?.isDealUp === true;
      isLow = this.valueChanges?.isDealUp === false;
    } else if (this.columnTypes.isDemand) {
      isHigh = this.valueChanges?.isDemandUp === true;
      isLow = this.valueChanges?.isDemandUp === false;
    }

    return {
      isHigh,
      isLow,
    };
  }

  public get hasHighlight(): boolean {
    const { bestBuy, bestLastDeal, bestSell, bestTotalPrice, bestTariff } =
      this.instrument;
    return (
      (bestSell && this.columnTypes.isOffer) ||
      (bestLastDeal && this.columnTypes.isDeal) ||
      (bestBuy && this.columnTypes.isDemand) ||
      (bestTotalPrice && this.columnTypes.isLocalPrice) ||
      (bestTariff && this.columnTypes.isTariff)
    );
  }

  public get columnData(): Data[] {
    const {
      quote,
      shipment,
      currentAmount,
      expectedAmount,
      previousSessionAmount,
      prevDealsAmountDiff,
      tariff,
      shipmentDays,
      deliveryDays,
      expectedTradingStart,
    } = this.instrument;

    const data: { [key: number]: Data[] } = {
      [MainTableColumnId.BASIS]: [shipment.code],
      [MainTableColumnId.BRIEF_DESCRIPTION]: [shipment.briefDescription],
      [MainTableColumnId.OFFER]: [quote.sellPrice, quote.sellAmount],
      [MainTableColumnId.DEAL]: [quote.lastDealPrice],
      [MainTableColumnId.DEMAND]: [quote.buyPrice, quote.buyAmount],
      [MainTableColumnId.CHANGES]: [quote.delta],
      [MainTableColumnId.VOLUME]: [currentAmount, expectedAmount],
      [MainTableColumnId.PREV_SESSION_VOLUME]: [previousSessionAmount],
      [MainTableColumnId.PREV_DEALS_AMOUNT_DIFF]: [prevDealsAmountDiff],
      [MainTableColumnId.TARIFF]: [tariff],
      [MainTableColumnId.LOCAL_PRICE]: [this._localPrice],
      [MainTableColumnId.SHIPMENT]: [shipmentDays],
      [MainTableColumnId.ON_THE_WAY]: [deliveryDays],
      [MainTableColumnId.TRADE_START]: [expectedTradingStart],
    };
    return data[this.columnId];
  }

  public get mode(): string {
    return this.instrument.mode;
  }

  public get instrumentCode() {
    return this.instrument.instrumentCode;
  }

  private get _isNeedPlug(): boolean {
    return (
      !this.columnTypes.isOffer &&
      !this.columnTypes.isDeal &&
      !this.columnTypes.isDemand &&
      !this.columnTypes.isVolume
    );
  }

  private get _localPrice(): Data {
    const { tariff, totalPrice } = this.instrument;
    return tariff ? totalPrice : null;
  }

  private get _isLastDeal(): boolean {
    return this.instrument.quote.dealStatus === SessionAgeStatus.LastSession;
  }

  private get _isOldDeal(): boolean {
    return this.instrument.quote.dealStatus === SessionAgeStatus.OldSession;
  }

  private get _isActialQuote() {
    return this.instrument.quote.quoteStatus === SessionAgeStatus.Actual;
  }

  private get _isLastQuote() {
    return this.instrument.quote.quoteStatus === SessionAgeStatus.LastSession;
  }

  private get _isOldQuote() {
    return this.instrument.quote.quoteStatus === SessionAgeStatus.OldSession;
  }
}
